import React, { useEffect, useState, useRef, useMemo } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import { doc, onSnapshot } from "firebase/firestore";
import { FiCreditCard, FiSearch, FiEye } from "react-icons/fi";
import { FaTimes, FaCheckCircle, FaExternalLinkAlt } from "react-icons/fa";
import {
  RiStore2Line,
  RiArchiveLine,
  RiCheckDoubleLine,
  RiStackLine,
  RiTimeLine,
} from "react-icons/ri";

const DEFAULT_LIMIT = 200;

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (ts) => {
  if (!ts) return "-";
  return (
    new Date(Number(ts)).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }) + " น."
  );
};

// Auto-detect search field type from input length
const detectSearchType = (value) => {
  const trimmed = value.trim();
  if (trimmed.length === 2) return "lastTwo";
  if (trimmed.length === 3) return "lastThree";
  if (trimmed.length === 6) return "number";
  if (trimmed.length > 6) return "fileNameId";
  return null;
};

const buildQueries = (roundDate, search, type, status) => {
  const queries = [["roundDate", "==", roundDate]];
  if (search && search.trim()) {
    const searchValue =
      type === "fileNameId" ? search.replace(/\s+/g, "") : search.trim();
    queries.push([type, "==", searchValue]);
  }
  switch (status) {
    case "sell":
      queries.push(["isOnBlock", "==", true], ["isSold", "==", false]);
      break;
    case "stock":
      queries.push(["isOnBlock", "==", false], ["isSold", "==", false]);
      break;
    case "sold":
      queries.push(["isSold", "==", true]);
      break;
    case "unsold":
      queries.push(["isSold", "==", false]);
      break;
    default:
      break;
  }
  return queries;
};

const showToast = (toast, success, successMsg, failMsg) =>
  toast({
    className: success
      ? "bg-emerald-600 text-white rounded-md"
      : "bg-rose-600 text-white rounded-md",
    title: (
      <div className="flex gap-2 items-center text-sm font-semibold">
        {success ? (
          <FaCheckCircle className="shrink-0" />
        ) : (
          <FaTimes className="shrink-0" />
        )}
        <span>
          {success ? successMsg : failMsg || "ผิดพลาด กรุณาลองใหม่อีกครั้ง"}
        </span>
      </div>
    ),
  });



// ── StatCard (Workspace Rules: text-xl font-bold, text-sm font-semibold, rounded-md)

const StatCard = ({ label, value, subtext, icon: Icon, badge, colorClass }) => (
  <div className="flex flex-col justify-between p-4 bg-white rounded-md shadow-xs border border-slate-200 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      {Icon && <Icon className={`text-base ${colorClass || "text-slate-400"}`} />}
    </div>
    <div className="mt-2 flex items-baseline justify-between">
      <div className="text-xl font-bold text-slate-900 tracking-tight">
        {value === "loading" ? (
          <Spinner className="w-4 h-4" />
        ) : (
          (Number(value) || 0).toLocaleString()
        )}
        <span className="text-xs font-semibold text-slate-400 ml-1">ใบ</span>
      </div>
      {badge && (
        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">
          {badge}
        </span>
      )}
    </div>
    {subtext && (
      <p className="text-[11px] font-medium text-slate-400 mt-1">{subtext}</p>
    )}
  </div>
);

// ── StatusSelect — per-row push/pull with optimistic update ───────────────────

const StatusSelect = ({ lottery, onUpdateStatus }) => {
  const [updating, setUpdating] = useState(false);

  if (lottery.isSold) {
    return (
      <a
        href={`/orders/${lottery.orderId}`}
        target="_blank"
        rel="noreferrer"
        className="w-36 h-8.5 inline-flex items-center justify-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 px-2 py-1 rounded-md text-sm font-bold transition-colors"
        title="คลิกเพื่อดูคำสั่งซื้อ"
      >
        {/* <span>ขายแล้ว</span> */}
        <span className="font-mono text-xs text-emerald-800">
          #{lottery.orderShortId || (lottery.orderId || "").slice(-5)}
        </span>
        <FaExternalLinkAlt className="text-[10px] opacity-70 shrink-0" />
      </a>
    );
  }

  const handleChange = async (e) => {
    const isPull = e.target.value === "pull";
    setUpdating(true);
    await onUpdateStatus(lottery, isPull);
    setUpdating(false);
  };

  return (
    <div className="relative inline-flex items-center">
      {updating ? (
        <div className="flex items-center justify-center w-36 h-8.5 bg-slate-100 rounded-md border border-slate-200">
          <Spinner className="w-4 h-4" />
        </div>
      ) : (
        <select
          className={`w-36 h-8.5 py-1 px-2.5 rounded-md text-center text-sm font-bold transition-colors cursor-pointer border ${lottery.isOnBlock
            ? "bg-sky-50 text-sky-700 border-sky-300 hover:bg-sky-100"
            : "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"
            }`}
          value={lottery.isOnBlock ? "push" : "pull"}
          onChange={handleChange}
        >
          <option value="push">วางขายแล้ว</option>
          <option value="pull">สต๊อกหลังบ้าน</option>
        </select>
      )}
    </div>
  );
};

// ── ImageModal (Lightbox for checking ticket details) ─────────────────────────

const ImageModal = ({ lottery, onClose }) => {
  if (!lottery) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-md shadow-2xl max-w-lg w-full p-5 border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              ตรวจสลากกินแบ่งรัฐบาล
            </h3>
            <p className="text-xs text-slate-500 font-semibold">
              งวด {lottery.roundDate} | เล่ม {lottery.fileNameId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <FaTimes className="text-base" />
          </button>
        </div>

        <div className="my-4 flex justify-center bg-slate-50 p-2 rounded-md border border-slate-100">
          <img
            src={lottery.image}
            alt={lottery.number}
            className="max-h-[340px] w-auto object-contain rounded-md shadow-xs"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded-md border border-slate-200/60">
          <div>
            <span className="text-slate-400 font-medium">หมายเลขสลาก:</span>
            <p className="font-mono font-bold text-slate-900 text-lg tracking-widest">
              {lottery.number}
            </p>
          </div>
          <div>
            <span className="text-slate-400 font-medium">สถานะ:</span>
            <p className="font-bold">
              {lottery.isSold ? (
                <span className="text-emerald-600">
                  ขายแล้ว (#{lottery.orderShortId || lottery.orderId?.slice(-5)})
                </span>
              ) : lottery.isOnBlock ? (
                <span className="text-sky-600">วางขายหน้าบ้าน</span>
              ) : (
                <span className="text-slate-600">สต๊อกหลังบ้าน</span>
              )}
            </p>
          </div>
          <div>
            <span className="text-slate-400 font-medium">ผู้นำเข้า:</span>
            <p className="font-semibold text-slate-800">{lottery.uploader || "-"}</p>
          </div>
          <div>
            <span className="text-slate-400 font-medium">วันที่นำเข้า:</span>
            <p className="font-semibold text-slate-800">
              {formatDate(lottery.createdRawAt)}
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="py-2 px-4 text-xs font-bold rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};

// ── LotteryRow ────────────────────────────────────────────────────────────────

const LotteryRow = ({
  lottery,
  index,
  isSelected,
  onToggleSelect,
  onUpdateStatus,
  onOpenPreview,
}) => (
  <TableRow
    className={`h-20 transition-colors ${isSelected ? "bg-sky-50/70" : "hover:bg-slate-50/60"
      }`}
  >
    <TableCell className="text-center w-14">
      {!lottery.isSold && (
        <input
          type="checkbox"
          id={lottery.id}
          className="w-5 h-5 text-navy rounded-md border-slate-300 focus:ring-navy cursor-pointer accent-navy"
          checked={isSelected}
          onChange={onToggleSelect}
        />
      )}
    </TableCell>
    <TableCell className="text-center w-40">
      <StatusSelect lottery={lottery} onUpdateStatus={onUpdateStatus} />
    </TableCell>
    <TableCell className="font-semibold text-center text-sm text-slate-600 w-16">
      {index + 1}
    </TableCell>
    <TableCell className="px-2 text-center w-44">
      <div className="flex flex-col items-center">
        <span className="font-mono font-bold text-slate-900 text-lg tracking-widest">
          {lottery.number}
        </span>
        <span className="text-xs text-slate-400 font-mono">
          {lottery.id.split("-").slice(0, 3).join("-")}
        </span>
      </div>
    </TableCell>
    <TableCell className="text-center w-32">
      <div
        className="relative group mx-auto w-24 h-12 rounded-md overflow-hidden border border-slate-200 cursor-pointer shadow-xs"
        onClick={() => onOpenPreview(lottery)}
        title="คลิกเพื่อขยายรูปภาพสลาก"
      >
        <img
          src={lottery.image}
          alt={lottery.number}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
          <FiEye className="text-base" />
        </div>
      </div>
    </TableCell>
    <TableCell className="font-mono text-center text-sm font-bold text-slate-800">
      {lottery.fileNameId}
    </TableCell>
    <TableCell className="text-center text-sm font-semibold text-slate-700">
      {lottery.uploader || "-"}
    </TableCell>
    <TableCell className="text-center text-sm font-semibold text-slate-600">
      {formatDate(lottery.createdRawAt)}
    </TableCell>
  </TableRow>
);

// ── ManageLotteries Main Component ───────────────────────────────────────────

export const ManageLotteries = () => {
  const { callApi, configs, db } = Context();
  const { toast } = useToast();

  const [lotteries, setLotteries] = useState([]);
  const [stats, setStats] = useState({
    total: "loading",
    onBlock: "loading",
    sold: "loading",
    offBlock: "loading",
    inCart: 0,
  });
  const [isLive, setIsLive] = useState(false);

  const [search, setSearch] = useState("");
  const [type, setType] = useState("number");
  const [status, setStatus] = useState("all");
  const [selectedLotto, setSelectedLotto] = useState([]);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [timeSortOrder, setTimeSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [previewLottery, setPreviewLottery] = useState(null);

  const lastIdRef = useRef(null);
  const sentinelRef = useRef(null);

  // ── 1. Real-time Firestore onSnapshot for Dashboard/{roundDate} (Only 1 Doc Read!) ──
  useEffect(() => {
    if (!db || !configs?.roundDate) return;

    try {
      const unsub = onSnapshot(
        doc(db, "Dashboard", String(configs.roundDate)),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setStats({
              total: Number(data.currentLotto) || 0,
              sold: Number(data.currentLottoSold) || 0,
              onBlock: Number(data.currentLottoOnBlock) || 0,
              offBlock: Number(data.currentLottoOffBlock) || 0,
              inCart: Number(data.currentLottoInCart) || 0,
            });
            setIsLive(true);
          }
        },
        (err) => {
          console.warn("ManageLotteries onSnapshot warning:", err);
          setIsLive(false);
        }
      );
      return () => unsub();
    } catch (err) {
      console.warn("ManageLotteries onSnapshot setup failed:", err);
      setIsLive(false);
    }
  }, [db, configs?.roundDate]);

  // ── 2. Fetch Lotteries ──────────────────────────────────────────────────────
  const getLotteries = async (overrideLimit) => {
    if (!configs?.roundDate) return;
    const currentLimit = typeof overrideLimit === "number" ? overrideLimit : limit;
    setLoading(true);
    lastIdRef.current = null;
    setSelectedLotto([]);

    const data = await callApi(
      "lotteries-getAll",
      {
        queries: buildQueries(configs.roundDate, search, type, status),
        roundDate: configs.roundDate,
        limit: currentLimit,
        orderBy: ["createdAt", "desc"],
      },
      { notShowLoading: true }
    );

    setLotteries(data || []);
    setHasMore((data || []).length === currentLimit);
    lastIdRef.current = (data || []).at(-1)?.id ?? null;
    if (!data) showToast(toast, false, null, "ดึงข้อมูลสลากผิดพลาด");
    setLoading(false);
  };

  const fetchMore = async () => {
    if (!lastIdRef.current || !configs?.roundDate) return;
    setLoadingMore(true);

    const data = await callApi(
      "lotteries-getAll",
      {
        queries: buildQueries(configs.roundDate, search, type, status),
        roundDate: configs.roundDate,
        limit: limit,
        orderBy: ["createdAt", "desc"],
        startAfter: lastIdRef.current,
      },
      { notShowLoading: true }
    );

    const page = data || [];
    setLotteries((prev) => [...prev, ...page]);
    setHasMore(page.length === limit);
    lastIdRef.current = page.at(-1)?.id ?? null;
    setLoadingMore(false);
  };

  // Initial load
  useEffect(() => {
    getLotteries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs?.roundDate, status]);

  // Infinite Scroll Sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, lotteries]);

  // ── 4. Optimistic Single Row Status Update ──────────────────────────────────
  const handleUpdateSingleStatus = async (lottery, isPull) => {
    // 1. Optimistic update table state
    setLotteries((prev) =>
      prev.map((item) =>
        item.id === lottery.id ? { ...item, isOnBlock: !isPull } : item
      )
    );

    // 2. Optimistic update stats card
    setStats((prev) => {
      if (typeof prev.onBlock !== "number") return prev;
      return {
        ...prev,
        onBlock: isPull ? Math.max(0, prev.onBlock - 1) : prev.onBlock + 1,
        offBlock: isPull ? prev.offBlock + 1 : Math.max(0, prev.offBlock - 1),
      };
    });

    // 3. API Call
    const apiName = isPull ? "blocks-pullFromBlock" : "blocks-pushToBlock";
    const body = isPull
      ? {
        lotterieIds: [lottery.id],
        isMagicNumber: lottery.isMagicNumber || false,
      }
      : {
        lotterieIds: [lottery.id],
      };

    const { status: ok, message } = await callApi(apiName, body, {
      notShowLoading: true,
    });

    if (ok) {
      showToast(
        toast,
        true,
        isPull
          ? `นำเลข ${lottery.number} ออกจากระบบแล้ว`
          : `วางขายเลข ${lottery.number} แล้ว`
      );
    } else {
      // Rollback on failure
      setLotteries((prev) =>
        prev.map((item) =>
          item.id === lottery.id ? { ...item, isOnBlock: lottery.isOnBlock } : item
        )
      );
      setStats((prev) => {
        if (typeof prev.onBlock !== "number") return prev;
        return {
          ...prev,
          onBlock: isPull ? prev.onBlock + 1 : Math.max(0, prev.onBlock - 1),
          offBlock: isPull ? Math.max(0, prev.offBlock - 1) : prev.offBlock + 1,
        };
      });
      showToast(toast, false, null, message || "ผิดพลาด ไม่สามารถเปลี่ยนสถานะได้");
    }
  };

  // ── 5. Sorted Lotteries by Import Time or LastTwo (when filtering by book) ─────────
  const sortedLotteries = useMemo(() => {
    if (type === "fileNameId") {
      return [...lotteries].sort((a, b) => {
        const valA = Number(a.lastTwo ?? (a.number ? a.number.slice(-2) : 0));
        const valB = Number(b.lastTwo ?? (b.number ? b.number.slice(-2) : 0));
        if (valA !== valB) return valA - valB;
        return String(a.number || "").localeCompare(String(b.number || ""));
      });
    }

    return [...lotteries].sort((a, b) => {
      const timeA =
        Number(a.createdRawAt) ||
        (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const timeB =
        Number(b.createdRawAt) ||
        (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return timeSortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });
  }, [lotteries, timeSortOrder, type]);

  const unsoldLotteries = useMemo(
    () => sortedLotteries.filter((x) => !x.isSold),
    [sortedLotteries]
  );

  const stockReadyCount = selectedLotto.filter(
    (x) => !x.isSold && !x.isOnBlock
  ).length;

  const blockReadyCount = selectedLotto.filter(
    (x) => !x.isSold && x.isOnBlock
  ).length;

  const isAllUnsoldSelected =
    unsoldLotteries.length > 0 &&
    unsoldLotteries.every((item) => selectedLotto.some((x) => x.id === item.id));

  const handleToggleSelectAll = () => {
    if (isAllUnsoldSelected) {
      setSelectedLotto([]);
    } else {
      setSelectedLotto(unsoldLotteries);
    }
  };

  const handlePushToBlock = async () => {
    const targets = selectedLotto.filter((x) => !x.isSold && !x.isOnBlock);
    if (targets.length === 0) return;

    setBulkLoading(true);
    const ids = targets.map((x) => x.id);
    const idSet = new Set(ids);

    // Optimistic update
    setLotteries((prev) =>
      prev.map((item) => (idSet.has(item.id) ? { ...item, isOnBlock: true } : item))
    );
    setStats((prev) => {
      if (typeof prev.onBlock !== "number") return prev;
      return {
        ...prev,
        onBlock: prev.onBlock + ids.length,
        offBlock: Math.max(0, prev.offBlock - ids.length),
      };
    });

    const { status: ok, count, message } = await callApi(
      "blocks-pushToBlock",
      { lotterieIds: ids },
      { notShowLoading: true }
    );

    setBulkLoading(false);
    if (ok) {
      showToast(toast, true, `วางขายแล้ว ${count || ids.length} ใบ`);
      setSelectedLotto([]);
    } else {
      showToast(toast, false, null, message || "วางขายไม่สำเร็จ");
      getLotteries();
    }
  };

  const handlePullFromBlock = async () => {
    const targets = selectedLotto.filter((x) => !x.isSold && x.isOnBlock);
    if (targets.length === 0) return;

    setBulkLoading(true);
    const ids = targets.map((x) => x.id);
    const idSet = new Set(ids);

    // Optimistic update
    setLotteries((prev) =>
      prev.map((item) => (idSet.has(item.id) ? { ...item, isOnBlock: false } : item))
    );
    setStats((prev) => {
      if (typeof prev.onBlock !== "number") return prev;
      return {
        ...prev,
        onBlock: Math.max(0, prev.onBlock - ids.length),
        offBlock: prev.offBlock + ids.length,
      };
    });

    const { status: ok, count, message } = await callApi(
      "blocks-pullFromBlock",
      { lotterieIds: ids },
      { notShowLoading: true }
    );

    setBulkLoading(false);
    if (ok) {
      showToast(toast, true, `ย้ายไปสต๊อกหลังบ้านแล้ว ${count || ids.length} ใบ`);
      setSelectedLotto([]);
    } else {
      showToast(toast, false, null, message || "ย้ายสต๊อกไม่สำเร็จ");
      getLotteries();
    }
  };

  // ── Stats Calculations ──────────────────────────────────────────────────────
  const soldPercent =
    typeof stats.total === "number" && stats.total > 0
      ? ((stats.sold / stats.total) * 100).toFixed(1)
      : undefined;

  const remainingCount =
    typeof stats.total === "number" && typeof stats.sold === "number"
      ? Math.max(0, stats.total - stats.sold)
      : "loading";

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12 space-y-4">
      {/* Page Header */}
      <Header
        header="Lotteries"
        subHeader={
          <div className="flex items-center gap-2 mt-0.5">
            <span>จัดการและควบคุมคลังสลากกินแบ่งรัฐบาล</span>
            {isLive ? (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-rose-50 border border-rose-200 text-rose-600 text-[10px] font-bold shadow-2xs"
                title="เชื่อมต่อ Real-time Firestore สำเร็จ (1 Doc Read)"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
                </span>
                <span>LIVE MONITOR</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                <span>OFFLINE</span>
              </span>
            )}
          </div>
        }
        icon={<FiCreditCard className="text-navy" />}
      />

      {/* 5 Real-time Stat Cards */}
      <div className="grid grid-cols-5 gap-3">
        <StatCard
          label="สลากทั้งหมด"
          value={stats.total}
          subtext={`งวด ${configs?.roundDate || "-"}`}
          icon={RiStackLine}
          colorClass="text-slate-600"
        />
        <StatCard
          label="สลากหน้าบ้าน (พร้อมขาย)"
          value={stats.onBlock}
          subtext="แสดงบนหน้าร้านลูกค้า"
          icon={RiStore2Line}
          colorClass="text-sky-600"
          badge={
            typeof stats.total === "number" && stats.total > 0
              ? `${((stats.onBlock / stats.total) * 100).toFixed(0)}%`
              : undefined
          }
        />
        <StatCard
          label="สลากหลังบ้าน (สต๊อก)"
          value={stats.offBlock}
          subtext="เก็บสำรองในคลัง"
          icon={RiArchiveLine}
          colorClass="text-amber-600"
        />
        <StatCard
          label="สลากที่ขายแล้ว"
          value={stats.sold}
          subtext="คำสั่งซื้อสำเร็จแล้ว"
          icon={RiCheckDoubleLine}
          colorClass="text-emerald-600"
          badge={soldPercent ? `${soldPercent}%` : undefined}
        />
        <StatCard
          label="สลากคงเหลือ (ยังไม่ขาย)"
          value={remainingCount}
          subtext="รวมหน้าบ้านและหลังบ้าน"
          icon={RiTimeLine}
          colorClass="text-rose-600"
        />
      </div>

      {/* Main Container */}
      <div className="relative bg-white w-full rounded-md shadow-xs border border-slate-200 overflow-hidden">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-1 p-3 border-b border-slate-200 bg-slate-50/50 overflow-x-auto">
          {[
            { id: "all", label: "สลากทั้งหมด", count: stats.total },
            { id: "sell", label: "สลากหน้าบ้าน", count: stats.onBlock },
            { id: "stock", label: "สลากหลังบ้าน", count: stats.offBlock },
            { id: "sold", label: "ขายแล้ว", count: stats.sold },
            { id: "unsold", label: "ยังไม่ขาย", count: remainingCount },
          ].map((tab) => {
            const isActive = status === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStatus(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all shrink-0 ${isActive
                  ? "bg-navy text-white shadow-xs"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                  }`}
              >
                <span>{tab.label}</span>
                {typeof tab.count === "number" && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-md ${isActive
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-500 font-mono"
                      }`}
                  >
                    {tab.count.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search & Filter Toolbar */}
        <div className="p-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search Input */}
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="ค้นหาเลข 2-6 หลัก หรือชื่อเล่ม..."
                value={search}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearch(val);
                  const detected = detectSearchType(val);
                  if (detected) setType(detected);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") getLotteries();
                }}
                className="w-64 h-9 pl-3 pr-8 rounded-md border border-slate-300 text-xs font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-hidden focus:border-navy focus:ring-1 focus:ring-navy"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setType("number");
                  }}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            {/* Type Dropdown */}
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-9 px-2.5 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-700 focus:outline-hidden focus:border-navy cursor-pointer"
            >
              <option value="number">เลข 6 หลัก</option>
              <option value="lastTwo">เลขท้าย 2 ตัว</option>
              <option value="lastThree">เลขท้าย 3 ตัว</option>
              <option value="firstThree">เลขหน้า 3 ตัว</option>
              <option value="fileNameId">ชื่อเล่มสลาก</option>
            </select>

            {/* Search Button */}
            <button
              disabled={loading}
              onClick={getLotteries}
              className="h-9 px-4 rounded-md bg-navy hover:bg-[#002b75] text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSearch className="text-sm" />
              <span>ค้นหา</span>
            </button>
          </div>

          {/* Table Count Summary & Limit Selector */}
          <div className="flex items-center gap-3 text-xs font-semibold text-slate-500">
            <div className="flex items-center gap-1.5">
              <span className="text-slate-600">แสดงรอบละ:</span>
              <select
                value={limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setLimit(newLimit);
                  getLotteries(newLimit);
                }}
                className="h-8 px-2 rounded-md border border-slate-300 bg-white text-xs font-bold text-slate-700 focus:outline-hidden focus:border-navy cursor-pointer"
              >
                <option value={100}>100 ใบ</option>
                <option value={200}>200 ใบ (เริ่มต้น)</option>
                <option value={500}>500 ใบ</option>
                <option value={1000}>1,000 ใบ</option>
              </select>
            </div>
            <span className="text-slate-300">|</span>
            <div>
              โหลดแล้ว {lotteries.length.toLocaleString()} ใบ
              {hasMore && " (เลื่อนลงเพื่อดูต่อ)"}
            </div>
          </div>
        </div>

        {/* Selected Items / Bulk Actions Banner */}
        {selectedLotto.length > 0 && (
          <div className="p-2.5 px-4 bg-sky-50 border-b border-sky-200 flex items-center justify-between gap-4 animate-in fade-in-0 duration-150">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
              <span className="px-2 py-0.5 rounded-md bg-navy text-white font-mono">
                {selectedLotto.length}
              </span>
              <span>ใบที่เลือก</span>
              <span className="text-slate-400 font-normal">|</span>
              <span className="text-sky-700 font-semibold">
                หน้าบ้าน {blockReadyCount} ใบ
              </span>
              <span className="text-slate-400 font-normal">,</span>
              <span className="text-amber-700 font-semibold">
                หลังบ้าน {stockReadyCount} ใบ
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={bulkLoading || stockReadyCount === 0}
                onClick={handlePushToBlock}
                className="py-1.5 px-3 rounded-md bg-navy hover:bg-[#002b75] text-white text-xs font-bold shadow-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {bulkLoading ? (
                  <Spinner className="w-3.5 h-3.5" color="border-white" />
                ) : null}
                <span>วางขายหน้าบ้าน ({stockReadyCount})</span>
              </button>

              <button
                disabled={bulkLoading || blockReadyCount === 0}
                onClick={handlePullFromBlock}
                className="py-1.5 px-3 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold shadow-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {bulkLoading ? (
                  <Spinner className="w-3.5 h-3.5" color="border-white" />
                ) : null}
                <span>ย้ายเข้าสต๊อกหลังบ้าน ({blockReadyCount})</span>
              </button>

              <button
                onClick={() => setSelectedLotto([])}
                className="py-1.5 px-2.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        )}

        {/* Lotteries Table */}
        <div className="relative min-h-[420px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex items-center justify-center z-20">
              <div className="flex flex-col items-center gap-2">
                <Spinner className="w-6 h-6" />
                <span className="text-xs font-bold text-slate-600">
                  กำลังโหลดรายการสลาก...
                </span>
              </div>
            </div>
          )}

          <Table>
            <TableHeader className="bg-slate-50 border-b border-slate-200">
              <TableRow>
                <TableHead className="text-center w-14 py-3">
                  <input
                    type="checkbox"
                    title="เลือกทั้งหมดในหน้านี้ (เฉพาะที่ยังไม่ขาย)"
                    className="w-5 h-5 text-navy rounded-md border-slate-300 focus:ring-navy cursor-pointer accent-navy"
                    checked={isAllUnsoldSelected}
                    onChange={handleToggleSelectAll}
                    disabled={unsoldLotteries.length === 0}
                  />
                </TableHead>
                <TableHead className="text-center w-40 font-bold text-slate-700 text-sm">
                  สถานะ
                </TableHead>
                <TableHead className="text-center w-16 font-bold text-slate-700 text-sm">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center w-44 font-bold text-slate-700 text-sm">
                  <div className="inline-flex items-center justify-center gap-1.5">
                    <span>เลขสลาก</span>
                    {type === "fileNameId" && (
                      <span className="text-xs px-2 py-0.5 rounded-md bg-navy text-white font-bold tracking-tight">
                        เรียง 00-99
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-center w-32 font-bold text-slate-700 text-sm">
                  รูปสลาก
                </TableHead>
                <TableHead className="text-center font-bold text-slate-700 text-sm">
                  ชื่อเล่ม
                </TableHead>
                <TableHead className="text-center font-bold text-slate-700 text-sm">
                  ผู้นำเข้า
                </TableHead>
                <TableHead
                  className={`text-center font-bold text-slate-700 text-sm ${type === "fileNameId"
                    ? "select-none"
                    : "cursor-pointer select-none hover:bg-slate-100 transition-colors"
                    }`}
                  onClick={() => {
                    if (type !== "fileNameId") {
                      setTimeSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
                    }
                  }}
                  title={
                    type === "fileNameId"
                      ? "เรียงตามเลขท้ายเล่ม (00-99)"
                      : "คลิกเพื่อสลับเรียงตามเวลานำเข้า (ล่าสุด / เก่าสุด)"
                  }
                >
                  <div className="inline-flex items-center justify-center gap-1">
                    <span>วันที่-เวลานำเข้า</span>
                    {type !== "fileNameId" && (
                      <span className="text-xs text-navy font-bold">
                        {timeSortOrder === "desc" ? "↓ ล่าสุด" : "↑ เก่าสุด"}
                      </span>
                    )}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedLotteries.length > 0 ? (
                <>
                  {sortedLotteries.map((lottery, index) => (
                    <LotteryRow
                      key={lottery.id}
                      lottery={lottery}
                      index={index}
                      isSelected={selectedLotto.some((x) => x.id === lottery.id)}
                      onToggleSelect={() =>
                        setSelectedLotto((prev) =>
                          prev.some((x) => x.id === lottery.id)
                            ? prev.filter((x) => x.id !== lottery.id)
                            : [...prev, lottery]
                        )
                      }
                      onUpdateStatus={handleUpdateSingleStatus}
                      onOpenPreview={setPreviewLottery}
                    />
                  ))}
                  <TableRow ref={sentinelRef}>
                    <TableCell colSpan={8} className="text-center py-5">
                      {loadingMore ? (
                        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-500">
                          <Spinner className="w-4 h-4" />
                          <span>กำลังโหลดสลากเพิ่มเติม...</span>
                        </div>
                      ) : hasMore ? (
                        <span className="text-sm text-slate-400 font-semibold">
                          เลื่อนลงเพื่อโหลดต่อ
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400 font-semibold">
                          สิ้นสุดรายการ ({lotteries.length.toLocaleString()} ใบ)
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <div className="w-full flex flex-col justify-center items-center h-80 gap-2 text-slate-400 font-semibold text-sm">
                      {!loading && (
                        <>
                          <FiSearch className="text-3xl text-slate-300" />
                          <span>ไม่พบรายการสลากที่ตรงตามเงื่อนไข</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Image Preview Lightbox Modal */}
      {previewLottery && (
        <ImageModal
          lottery={previewLottery}
          onClose={() => setPreviewLottery(null)}
        />
      )}
    </div>
  );
};
